import React, { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import useAuth from './hooks/useAuth'
import { ThemeProvider } from './hooks/useTheme'
import ModernLayout from './components/layout/ModernLayout'
import LoadingSpinner from './components/ui/LoadingSpinner'
import { CompanyRestrictionsProvider } from './hooks/useCompanyRestrictions.jsx'

// Lazy loading de páginas principales
const Login = lazy(() => import('./pages/Login'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Pedidos = lazy(() => import('./pages/Pedidos'))
const Reportes = lazy(() => import('./pages/Reportes'))
const Alertas = lazy(() => import('./pages/Alertas'))
const Precios = lazy(() => import('./pages/Precios'))
const Facturas = lazy(() => import('./pages/Facturas'))

const Configuracion = lazy(() => import('./pages/Configuracion'))
const AdsPageWithTabs = lazy(() => import('./pages/ads/AdsPageWithTabs'))
const AdsPageTest = lazy(() => import('./pages/ads/AdsPageTest'))
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'))
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard'))

// Componente para rutas protegidas con verificación de permisos
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Si se especifican roles permitidos, verificar que el usuario tenga uno de esos roles
  if (allowedRoles.length > 0) {
    const userRole = user?.role || 'cliente'
    // Mapear "viewer" a "admin" para compatibilidad
    const effectiveRole = userRole === 'viewer' ? 'admin' : userRole
    if (!allowedRoles.includes(effectiveRole)) {
      // Redirigir al dashboard si no tiene permisos
      return <Navigate to="/dashboard" replace />
    }
  }

  return (
    <ModernLayout>
      <Suspense fallback={<LoadingSpinner message="Cargando página..." />}>
        {children}
      </Suspense>
    </ModernLayout>
  )
}

// Componente para rutas públicas (solo si no está autenticado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function App() {
  return (
    <ThemeProvider>
      <CompanyRestrictionsProvider>
        <div className="App">
          <Routes
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
          {/* Rutas públicas */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Suspense fallback={<LoadingSpinner message="Cargando login..." />}>
                  <Login />
                </Suspense>
              </PublicRoute>
            } 
          />

          <Route 
            path="/reset-password" 
            element={
              <PublicRoute>
                <Suspense fallback={<LoadingSpinner message="Cargando..." />}>
                  <ResetPassword />
                </Suspense>
              </PublicRoute>
            } 
          />

          {/* Rutas protegidas */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/pedidos" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador', 'cliente', 'dto_cuentas']}>
                <Pedidos />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/reportes"
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador', 'cliente', 'dto_cuentas']}>
                <Reportes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/alertas"
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador', 'cliente', 'dto_cuentas']}>
                <Alertas />
              </ProtectedRoute>
            }
          />

          <Route
            path="/precios"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Precios />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/facturas" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador', 'dto_cuentas']}>
                <Facturas />
              </ProtectedRoute>
            } 
          />



          <Route
            path="/ads"
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador', 'cliente', 'dto_cuentas']}>
                <AdsPageWithTabs />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/ads-test" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador', 'cliente']}>
                <AdsPageTest />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/configuracion"
            element={
              <ProtectedRoute allowedRoles={['admin', 'dto_cuentas']}>
                <Configuracion />
              </ProtectedRoute>
            }
          />

          <Route
            path="/executive"
            element={
              <ProtectedRoute allowedRoles={['admin', 'trabajador']}>
                <ExecutiveDashboard />
              </ProtectedRoute>
            }
          />

          {/* Redirecciones */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </div>
      </CompanyRestrictionsProvider>
    </ThemeProvider>
  )
}

export default App
